import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useMutationDeleteStaff,
  useMutationHostAvailabilityEdit,
  useStaffDisable,
  useStaffEnable,
} from "@gymflow/api";
import { UserStaffBean } from "@gymflow/types";
import { copyToClipboard } from "apps/portal/src/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { ModalContext } from "apps/portal/src/providers";
import { RouteFeature } from "apps/portal/src/routes/feature";
import useGymflowModels from "apps/portal/src/store";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { RouteLayout } from "../../../routes/layout";
import {
  Button,
  CheckCircleIcon,
  CopyIcon,
  EditIcon,
  MenuPopover,
  MenuPopoverOptionType,
} from "../../atoms";
import { CalendarDateIcon } from "../../atoms/icons/CalendarDateIcon";
import { CancelIcon } from "../../atoms/icons/CancelIcon";
import { Trash3Icon } from "../../atoms/icons/Trash3Icon";
import { ConfirmModal } from "../../templates";

type StaffActionsMenuPopoverProps = {
  staff: UserStaffBean;
  setEditingStaffId: (id?: string) => void;
};

export const StaffActionsMenuPopover = ({
  staff,
  setEditingStaffId,
}: StaffActionsMenuPopoverProps) => {
  const { t } = useTranslation();
  const { setModal, hide } = useContext(ModalContext);
  const history = useHistory();
  const { createClubLink, createAbsoluteClubLink } = usePortalRoutes();

  const { api } = useGymflowModels();
  const enableStaff = useStaffEnable({ api });
  const disableStaff = useStaffDisable({ api });
  const editAvailabilityMutation = useMutationHostAvailabilityEdit({ api });
  const { mutateAsync: mutateAsyncDeleteStaff } = useMutationDeleteStaff({
    api,
  });

  const confirmModalTranslationKeys = useMemo(
    () => ({
      enable: {
        name: t("page.settings.tab.staff.table.actions.enable.name"),
        title: t(
          "page.settings.tab.staff.table.actions.enable.confirmModal.title",
        ),
        description: t(
          "page.settings.tab.staff.table.actions.enable.confirmModal.description",
        ),
        additionalInfo: t(
          "page.settings.tab.staff.table.actions.enable.confirmModal.additionalInfo",
        ),
      },
      disable: {
        name: t("page.settings.tab.staff.table.actions.disable.name"),
        title: t(
          "page.settings.tab.staff.table.actions.disable.confirmModal.title",
        ),
        description: t(
          "page.settings.tab.staff.table.actions.disable.confirmModal.description",
        ),
        additionalInfo: t(
          "page.settings.tab.staff.table.actions.disable.confirmModal.additionalInfo",
        ),
      },
    }),
    [t],
  );

  const bookingLinkOption: MenuPopoverOptionType = {
    id: "bookingLink",
    label: t("page.settings.tab.staff.table.copyLink"),
    icon: <CopyIcon className="size-5 stroke-gray-500" />,
    onClick: async () => {
      await copyToClipboard(
        createAbsoluteClubLink(
          RouteLayout.Site,
          `${RouteFeature.SiteTimetableType.replace(":viewType", "list")}?host=${staff.id}`,
        ),
      );
    },
  };

  const menuOptions = useMemo<MenuPopoverOptionType[]>(() => {
    const id = staff.id;
    const isEnabled = staff.activeUser;
    const prefix = isEnabled ? "disable" : "enable";

    const options: MenuPopoverOptionType[] = [
      {
        id: "availability",
        label: t("page.settings.tab.staff.table.actions.availability"),
        icon: (
          <CalendarDateIcon
            className="size-5"
            pathClassName="stroke-gray-500"
          />
        ),
        onClick: () => {
          history.push(
            createClubLink(
              RouteLayout.Staff,
              RouteFeature.StaffAvailability.replace(":staffId", id),
            ),
          );
        },
      },

      {
        id: "edit",
        label: t("page.settings.tab.staff.table.actions.edit"),
        icon: <EditIcon className="size-5" pathClassName="stroke-gray-500" />,
        onClick: () => setEditingStaffId(id),
      },
      {
        id: "toggle",
        label: confirmModalTranslationKeys[prefix].name,
        classNameLabel: isEnabled ? "text-error-600" : "text-success-600",
        icon: isEnabled ? (
          <CancelIcon className="size-5" pathClassName="stroke-error-600" />
        ) : (
          <CheckCircleIcon
            className="size-5"
            pathClassName="stroke-success-600"
          />
        ),
        topDivider: true,
        onClick: () => {
          setModal(
            <ConfirmModal
              type={isEnabled ? "danger" : "secondary"}
              title={confirmModalTranslationKeys[prefix].title}
              onConfirm={async () => {
                if (isEnabled) {
                  await disableStaff.mutateAsync(id);

                  await editAvailabilityMutation.mutateAsync({
                    staffId: id,
                    params: { availabilitySlotList: [] },
                  });
                } else {
                  await enableStaff.mutateAsync(id);
                }
                hide();
              }}
              onCancel={hide}
            >
              <>
                <div className="mt-2 text-sm text-gray-600">
                  {confirmModalTranslationKeys[prefix].description}
                </div>
                <div className="text-sm text-gray-600">
                  {confirmModalTranslationKeys[prefix].additionalInfo}
                </div>
              </>
            </ConfirmModal>,
          );
        },
      },
      {
        id: "delete",
        label: t("portal.common.delete"),
        classNameLabel: "text-error-600",
        icon: <Trash3Icon className="size-5" />,
        onClick: () => {
          setModal(
            <ConfirmModal
              title={t(
                "page.settings.tab.staff.table.actions.delete.confirmModal.title",
              )}
              type="danger"
              onConfirm={async () => {
                await mutateAsyncDeleteStaff(id);

                hide();
              }}
              onCancel={hide}
            >
              {t(
                "page.settings.tab.staff.table.actions.delete.confirmModal.description",
              )}
            </ConfirmModal>,
          );
        },
      },
    ];

    return options;
  }, [
    staff.id,
    staff.activeUser,
    t,
    confirmModalTranslationKeys,
    history,
    createClubLink,
    setEditingStaffId,
    setModal,
    hide,
    disableStaff,
    editAvailabilityMutation,
    enableStaff,
    mutateAsyncDeleteStaff,
  ]);

  const isSmallScreen = window.innerWidth < 1024;
  if (isSmallScreen) {
    menuOptions.splice(1, 0, bookingLinkOption);
  }

  return (
    <MenuPopover
      classNameContent="w-48"
      triggerButton={
        <Button className="size-9 p-0">
          <FontAwesomeIcon
            icon={faEllipsisV}
            className="size-4 text-gray-950"
          />
        </Button>
      }
      options={menuOptions}
    />
  );
};
